/**
 * Stops Sentry Replay when a client-side navigation enters an MCP OAuth flow
 * (tmcp U1, Codex r2 R2-F1). A page *load* inside an OAuth flow never installs
 * Replay (entry.client.tsx, `replayAllowedForPageLoad`); this covers a session
 * that started on an ordinary page and then navigates — or is redirected by a
 * loader — into `/oauth/*` or the `/login?redirectUrl=<consent URL>` handoff.
 *
 * Why the root component and `useNavigation`: React Router exposes the
 * pending location (`navigation.location`) as soon as the navigation starts
 * (and again for each loader redirect), before the target route's loaders
 * resolve and before its DOM is rendered, so Replay is stopped while the
 * recorder has only seen the previous, non-OAuth page. The committed
 * location is checked too as a backstop. `Sentry.getReplay()` is undefined
 * when Replay was never installed (non-production, or an OAuth page load),
 * and `stop()` is idempotent; once stopped, Replay stays off for the rest of
 * the page's life.
 *
 * Second layer only (Codex r3 R3-F1): the stop runs in an effect, after
 * commit, so a coalesced navigation could paint the consent page before it.
 * The consent card root is Replay-blocked (`data-sentry-block`, see
 * OAUTH_REPLAY_BLOCK_SELECTORS), so the recorder never serialises its DOM
 * whatever the timing; this guard then stops the recording altogether.
 */
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { useLocation, useNavigation } from "react-router";
import { isOauthFlowUrl } from "./oauth-url-redaction";

type RouterLocation = { pathname: string; search: string; hash: string };

/** True when a router location is part of an OAuth flow (see `isOauthFlowUrl`). */
export function isOauthFlowLocation(location: RouterLocation | undefined | null): boolean {
  if (!location) return false;
  return isOauthFlowUrl(`${location.pathname}${location.search}${location.hash}`);
}

export function useStopReplayOnOauthNavigation(): void {
  const location = useLocation();
  const navigation = useNavigation();
  const inOauthFlow =
    isOauthFlowLocation(navigation.location) || isOauthFlowLocation(location);
  useEffect(() => {
    if (!inOauthFlow) return;
    void Sentry.getReplay()?.stop();
  }, [inOauthFlow]);
}
